import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Dropdown item interface.
 */
export interface DropdownItem {
  id: string;
  text: string;
  data?: unknown;
  isCreateOption?: boolean;
  className?: string;
  /**
   * When defined, renders a checkbox before the item text.
   * `true` means the item is currently selected (checked); `false` means unselected.
   * When `undefined`, no checkbox is rendered (existing non-checkbox behaviour).
   */
  checked?: boolean;
}

/**
 * Calculated dropdown position with all positioning properties.
 */
interface CalculatedDropdownPosition {
  calculated: true;
  left: number;
  top: number;
  width: number;
  showAbove: boolean;
}

/**
 * Dropdown position state - discriminated union for type safety.
 * Can be either uncalculated (initial state) or calculated with position data.
 */
type DropdownPositionState = { calculated: false } | CalculatedDropdownPosition;

/**
 * Props for the dropdown portal component.
 */
export interface DropdownPortalProps {
  isOpen: boolean;
  items: DropdownItem[];
  onSelect: (item: DropdownItem) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  maxHeight?: number;
  zIndex?: number;
  className?: string;
  backgroundColor?: string;
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
}

/**
 * Calculate dropdown position relative to anchor element.
 * When positioning above, the top is set to the anchor's top edge,
 * and the transform translateY(-100%) moves it up by its own height.
 * When positioning below, the top is set to the anchor's bottom edge.
 *
 * @param anchorEl - The anchor HTML element to position the dropdown relative to
 * @param dropdownHeight - The height of the dropdown to determine if it fits below or needs to be shown above
 * @returns The calculated dropdown position with all positioning properties
 */
const calculatePosition = (
  anchorEl: HTMLElement,
  dropdownHeight: number,
): CalculatedDropdownPosition => {
  const rect = anchorEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Position below by default, above if not enough space
  const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  return {
    calculated: true,
    left: rect.left,
    top: showAbove ? rect.top : rect.bottom,
    width: rect.width,
    showAbove,
  };
};

/**
 * DropdownPortal Component
 * A reusable dropdown component that renders as a portal for proper z-index handling
 * and provides keyboard navigation support
 *
 * @param props - Component props.
 * @param props.isOpen - Whether the dropdown is visible
 * @param props.items - Array of items to display
 * @param props.onSelect - Callback when an item is selected
 * @param props.onClose - Callback when dropdown should close
 * @param props.anchorRef - Reference element to position the dropdown relative to
 * @param props.maxHeight - Maximum height of the dropdown (default: 200)
 * @param props.zIndex - Z-index for the dropdown (default: 10000)
 * @param props.className - Custom CSS class for the dropdown
 * @param props.backgroundColor - Background color for the dropdown (default: "var(--bs-body-bg)")
 * @param props.selectedIndex - Currently selected item index (for keyboard navigation)
 * @param props.onSelectedIndexChange - Callback when selected index changes
 */
export const DropdownPortal = ({
  isOpen,
  items,
  onSelect,
  onClose,
  anchorRef,
  maxHeight = 200,
  zIndex = 10000,
  className = "",
  backgroundColor = "var(--bs-body-bg)",
  selectedIndex = -1,
  onSelectedIndexChange,
}: DropdownPortalProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<DropdownPositionState>({ calculated: false });

  /**
   * Update dropdown position when anchor changes or dropdown opens
   */
  const updatePosition = useCallback(() => {
    /* istanbul ignore else */
    if (anchorRef.current && isOpen) {
      const newPosition = calculatePosition(anchorRef.current, maxHeight);
      setPosition(newPosition);
    }
  }, [anchorRef, isOpen, maxHeight]);

  /**
   * Update position on open and window resize/scroll
   */
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, updatePosition]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || items.length === 0) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          e.stopPropagation();
          const nextIndex = Math.min(selectedIndex + 1, items.length - 1);
          onSelectedIndexChange?.(nextIndex);
          break;
        }

        case "ArrowUp": {
          e.preventDefault();
          e.stopPropagation();
          const prevIndex = Math.max(selectedIndex - 1, 0);
          onSelectedIndexChange?.(prevIndex);
          break;
        }

        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            onSelect(items[selectedIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    },
    [isOpen, items, selectedIndex, onSelect, onClose, onSelectedIndexChange],
  );

  /**
   * Set up keyboard event listeners
   */
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  /**
   * Scroll selected item into view
   */
  useEffect(() => {
    if (isOpen && selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      /* istanbul ignore else */
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [isOpen, selectedIndex]);

  useEffect(() => {
    if (!isOpen) return;

    /**
     * Handle click outside to close dropdown.
     *
     * @param e - The event object
     */
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // Don't close if clicking on anchor element or dropdown
      if (anchorRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  /**
   * Don't render if not open or no items or position not calculated yet
   */
  if (!isOpen || items.length === 0 || !position.calculated) {
    return null;
  }

  /**
   * Render dropdown content
   */
  const dropdownContent = (
    <div
      ref={dropdownRef}
      role="menu"
      className={`dropdown-menu show position-fixed ${className}`}
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight,
        overflowY: "auto",
        zIndex,
        backgroundColor,
        transform: position.showAbove ? "translateY(-100%)" : "none",
      }}
    >
      {items.map((item, index) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={`dropdown-item d-flex align-items-center ${
            index === selectedIndex ? "active" : ""
          } ${item.className ?? ""}`}
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent input blur
            onSelect(item);
          }}
          style={{
            fontStyle: item.isCreateOption ? "italic" : "normal",
            cursor: "pointer",
            backgroundColor: index === selectedIndex ? "var(--bs-primary)" : undefined,
            color: index === selectedIndex ? "var(--bs-primary-text)" : undefined,
          }}
        >
          {item.checked !== undefined && (
            <input
              type="checkbox"
              checked={item.checked}
              readOnly
              className="form-check-input me-2 flex-shrink-0"
              tabIndex={-1}
              style={{ cursor: "pointer", pointerEvents: "none" }}
            />
          )}
          {item.text}
        </button>
      ))}
    </div>
  );

  /**
   * Render as portal to document body
   */
  return createPortal(dropdownContent, document.body);
};

DropdownPortal.displayName = "DropdownPortal";
